import { AfterViewInit, Directive, ElementRef, HostListener, OnDestroy } from '@angular/core';

/**
 * ng-multiselect-dropdown has no keyboard support: the trigger div is
 * focusable (tabindex="0") but only opens via a (click) on its inner
 * .dropdown-btn span, and the library closes the panel on any blur of
 * the trigger, which made the options unreachable by keyboard.
 *
 * This directive bridges that gap without forking the library:
 *  - Enter/Space on the trigger opens the panel and moves focus into it
 *    (search input if present, else first option checkbox).
 *  - ArrowDown/ArrowUp move focus between option checkboxes while open.
 *  - Esc closes the panel and restores focus to the trigger.
 *  - Focus leaving the component (Tab past the last option, Shift+Tab
 *    before the first) closes the panel.
 *  - A capture-phase blur listener suppresses the library's blur-close
 *    ONLY while focus moves within the component, so Tab can walk the
 *    options.
 *  - A focusin listener scrolls the focused option into view: the real
 *    <input> is visually hidden, so the browser's native scroll-on-focus
 *    targets the wrong element and the list doesn't follow keyboard focus.
 * WCAG: SC 2.1.1 (keyboard operable), SC 2.1.2 (no keyboard trap).
 */
@Directive({
  selector: 'ng-multiselect-dropdown'
})
export class MultiselectKeyboardDirective implements AfterViewInit, OnDestroy {

  private blurInterceptor: (e: FocusEvent) => void;

  constructor(private el: ElementRef) {}

  ngAfterViewInit() {
    const host = this.el.nativeElement;

    this.blurInterceptor = (e: FocusEvent) => {
      const trigger = host.querySelector('.multiselect-dropdown');
      const related = e.relatedTarget as HTMLElement;
      // Blur is leaving the trigger, but focus is staying inside this
      // component (e.g. moving into the options list): block the
      // library's blur-close handler.
      if (e.target === trigger && related && host.contains(related)) {
        e.stopPropagation();
      }
    };

    // Capture phase: runs before the library's own blur listener on the trigger
    host.addEventListener('blur', this.blurInterceptor, true);
  }

  ngOnDestroy() {
    this.el.nativeElement.removeEventListener('blur', this.blurInterceptor, true);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(e: KeyboardEvent) {
    const host = this.el.nativeElement;
    const trigger: HTMLElement = host.querySelector('.multiselect-dropdown');
    const btn: HTMLElement = host.querySelector('.dropdown-btn');
    if (!trigger || !btn) { return; }

    const isOpen = !!host.querySelector('.dropdown-list:not([hidden])');
    const key = e.key;
    const target = e.target as HTMLElement;

    // Open (or close) with Enter/Space when focus is on the trigger itself
    if ((key === 'Enter' || key === ' ' || key === 'Spacebar') && target === trigger) {
      e.preventDefault(); // stop Space from scrolling the page
      btn.click();
      if (!isOpen) {
        // Move focus into the panel so Tab walks the options
        setTimeout(() => {
          const first: HTMLElement = host.querySelector(
            '.dropdown-list:not([hidden]) input[type="text"], ' +
            '.dropdown-list:not([hidden]) input[type="checkbox"]'
          );
          if (first) { first.focus(); }
        });
      }
    }

    // ArrowDown/ArrowUp move focus between options while the panel is open.
    // (Convenience on top of Tab, not a full ARIA listbox pattern.)
    if ((key === 'ArrowDown' || key === 'ArrowUp' || key === 'Down' || key === 'Up') && isOpen) {
      const inList = !!target.closest('.dropdown-list');
      if (inList || target === trigger) {
        e.preventDefault(); // stop page scroll
        const items: HTMLElement[] = Array.prototype.slice.call(
          host.querySelectorAll('.dropdown-list:not([hidden]) input[type="checkbox"]')
        );
        if (items.length === 0) { return; }

        const down = (key === 'ArrowDown' || key === 'Down');
        const idx = items.indexOf(target);
        let next: HTMLElement;
        if (idx === -1) {
          // Coming from the trigger or the search input: enter the list
          next = down ? items[0] : items[items.length - 1];
        } else {
          next = down
            ? items[Math.min(idx + 1, items.length - 1)]
            : items[Math.max(idx - 1, 0)];
        }
        next.focus();
      }
    }

    // Dismiss with Esc from anywhere inside, restoring focus to the trigger
    if ((key === 'Escape' || key === 'Esc') && isOpen) {
      e.preventDefault();
      e.stopPropagation(); // don't bubble Esc to a parent modal
      btn.click();
      trigger.focus();
    }
  }

  // Close the panel when focus exits the component entirely
  // (Tab past the last option, or Shift+Tab before the first)
  @HostListener('focusout', ['$event'])
  onFocusOut(e: FocusEvent) {
    const host = this.el.nativeElement;
    const related = e.relatedTarget as HTMLElement;
    if (!related || !host.contains(related)) {
      const isOpen = !!host.querySelector('.dropdown-list:not([hidden])');
      if (isOpen) {
        const btn: HTMLElement = host.querySelector('.dropdown-btn');
        if (btn) { btn.click(); }
      }
    }
  }

  // Keep the focused option visible: the real checkbox <input> is visually
  // hidden, so the browser scrolls to it rather than to the <li> the user
  // actually sees, leaving keyboard focus "invisible" in a scrolled list.
  @HostListener('focusin', ['$event'])
  onFocusIn(e: FocusEvent) {
    const target = e.target as HTMLElement;
    if (!target || !target.closest) { return; }
    const row = target.closest('.dropdown-list li') as HTMLElement;
    if (row && typeof row.scrollIntoView === 'function') {
      row.scrollIntoView({ block: 'nearest' });
    }
  }
}
